import React, { useState, useEffect } from 'react';
import api, { tasksAPI, businessesAPI } from '../services/api';
import { format, isPast, parseISO } from 'date-fns';
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  normal: 'bg-blue-100 text-blue-800 border-blue-300',
  low: 'bg-gray-100 text-gray-800 border-gray-300',
};

const STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Safe helpers to avoid render-time crashes
const safeFormatDate = (value, fmt = 'MMM dd, yyyy') => {
  try {
    if (!value) return '—';
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return format(d, fmt);
  } catch {
    return '—';
  }
};

const safeArray = (arr) => (Array.isArray(arr) ? arr : []);
const safePct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};
const priorityClass = (p) => PRIORITY_COLORS[p] || PRIORITY_COLORS.normal;
const statusClass = (s) => STATUS_COLORS[s] || STATUS_COLORS.not_started;

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [completionDrafts, setCompletionDrafts] = useState({});
  const [formError, setFormError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const validateForm = (data) => {
    const errs = {};
    if (!data.task_name || data.task_name.trim() === '') {
      errs.task_name = 'Title is required.';
    }
    if (data.completion_percentage !== undefined && data.completion_percentage !== null) {
      const val = parseInt(data.completion_percentage, 10);
      if (Number.isNaN(val) || val < 0 || val > 100) {
        errs.completion_percentage = 'Completion must be between 0 and 100.';
      }
    }
    // Checklist validation is now handled by the dynamic interface
    // No need to validate JSON format
    const allowedPriority = ['low','normal','high','urgent'];
    if (data.priority && !allowedPriority.includes(data.priority)) {
      errs.priority = 'Invalid priority.';
    }
    const allowedStatus = ['not_started','in_progress','completed','on_hold','cancelled'];
    if (data.status && !allowedStatus.includes(data.status)) {
      errs.status = 'Invalid status.';
    }
    return errs;
  };

  // Form state
  const [formData, setFormData] = useState({
    business_id: '',
    task_name: '',
    description: '',
    priority: 'normal',
    status: 'not_started',
    due_date: '',
    completion_percentage: 0,
    assigned_to: '',
    tags: '',
    checklist: '',
    related_entity_type: '',
    related_entity_id: '',
    is_active: true,
  });
  const [checklistItems, setChecklistItems] = useState([]);

  useEffect(() => {
    loadData();
  }, [filterPriority, filterStatus, showOverdueOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(filterPriority && { priority: filterPriority }),
        ...(filterStatus && { status: filterStatus }),
      };

      let tasksPromise;
      if (showOverdueOnly) {
        tasksPromise = tasksAPI.getOverdue(params);
      } else {
        tasksPromise = tasksAPI.getAll(params);
      }

      const [tasksRes, businessesRes, statsRes] = await Promise.all([
        tasksPromise,
        businessesAPI.getAll(),
        tasksAPI.getStats(),
      ]);
      
      setTasks(tasksRes.data);
      setBusinesses(businessesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading tasks:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setFormError('');
      const errs = validateForm(formData);
      setFormErrors(errs);
      if (Object.keys(errs).length > 0) return;
      // Convert checklistItems to the format expected by backend
      const checklistData = checklistItems.length > 0 
        ? checklistItems.filter(item => item.text.trim() !== '')
        : null;
      
      const submitData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : null,
        checklist: checklistData,
      };

      if (editingTask) {
        await tasksAPI.update(editingTask.id, submitData);
      } else {
        await tasksAPI.create(submitData);
      }
      setShowModal(false);
      resetForm();
      loadData();
      setToast('Task saved');
      setTimeout(() => setToast(''), 2500);
    } catch (error) {
      console.error('Error saving task:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      const apiMsg = formatApiError(error, 'Failed to save task');
      setFormError(apiMsg);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    // Parse checklist if it exists
    let parsedChecklist = [];
    if (task.checklist) {
      try {
        parsedChecklist = Array.isArray(task.checklist) 
          ? task.checklist 
          : JSON.parse(task.checklist);
      } catch (e) {
        parsedChecklist = [];
      }
    }
    setChecklistItems(parsedChecklist);
    setFormData({
      business_id: task.business_id || '',
      task_name: task.task_name,
      description: task.description || '',
      priority: task.priority,
      status: task.status,
      due_date: task.due_date ? format(parseISO(task.due_date), 'yyyy-MM-dd') : '',
      completion_percentage: task.completion_percentage || 0,
      assigned_to: task.assigned_to || '',
      tags: task.tags ? task.tags.join(', ') : '',
      checklist: '', // Will be generated from checklistItems
      related_entity_type: task.related_entity_type || '',
      related_entity_id: task.related_entity_id || '',
      is_active: task.is_active,
    });
    setShowModal(true);
  };
  
  const resetForm = () => {
    setFormData({
      business_id: '',
      task_name: '',
      description: '',
      priority: 'normal',
      status: 'not_started',
      due_date: '',
      completion_percentage: 0,
      assigned_to: '',
      tags: '',
      checklist: '',
      related_entity_type: '',
      related_entity_id: '',
      is_active: true,
    });
    setChecklistItems([]);
    setEditingTask(null);
    setFormError('');
    setFormErrors({});
  };
  
  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, { text: '', completed: false }]);
  };
  
  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };
  
  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index] = { ...updated[index], [field]: value };
    setChecklistItems(updated);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting task:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete task');
    }
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return isPast(parseISO(task.due_date));
  };

  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.id === businessId);
    return business ? business.name : 'N/A';
  };

  const setDraftCompletion = (taskId, value) => {
    const v = Math.max(0, Math.min(100, parseInt(value) || 0));
    setCompletionDrafts((prev) => ({ ...prev, [taskId]: v }));
  };

  const saveCompletion = async (taskId) => {
    try {
      const value = completionDrafts[taskId];
      if (value === undefined) return;
      await tasksAPI.patchCompletion(taskId, value);
      const updated = tasks.map(t => t.id === taskId ? { ...t, completion_percentage: value, status: value === 100 ? 'completed' : value === 0 ? 'not_started' : 'in_progress' } : t);
      setTasks(updated);
    } catch (err) {
      console.error('Failed to update completion:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      alert('Failed to update completion');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div aria-live="polite" className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded shadow">
          {toast}
        </div>
      )}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Task
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Total Tasks</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_tasks || 0}</div>
          </div>
          <div className="bg-green-50 rounded-lg shadow p-6">
            <div className="text-sm text-green-600 mb-1">Completed</div>
            <div className="text-3xl font-bold text-green-900">{stats.completed_tasks || 0}</div>
          </div>
          <div className="bg-blue-50 rounded-lg shadow p-6">
            <div className="text-sm text-blue-600 mb-1">In Progress</div>
            <div className="text-3xl font-bold text-blue-900">{stats.in_progress_tasks || 0}</div>
          </div>
          <div className="bg-red-50 rounded-lg shadow p-6">
            <div className="text-sm text-red-600 mb-1">Overdue</div>
            <div className="text-3xl font-bold text-red-900">{stats.overdue_tasks || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showOverdueOnly}
                onChange={(e) => setShowOverdueOnly(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
              />
              <span className="text-sm text-gray-900">Show Overdue Only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`bg-white rounded-lg shadow p-6 border-l-4 ${
              isOverdue(task) ? 'border-red-500' : 'border-blue-500'
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <ErrorBoundary>
                    <h3 className="text-xl font-bold text-gray-900">{task.task_name}</h3>
                  </ErrorBoundary>
                  {isOverdue(task) && (
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                  )}
                  {task.status === 'completed' && (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  )}
                </div>
                <ErrorBoundary>
                  <p className="text-gray-600 mb-3">{task.description}</p>
                </ErrorBoundary>
                <div className="flex flex-wrap gap-2 mb-3">
                  <ErrorBoundary>
                    <span className={`px-3 py-1 text-xs rounded-full border ${priorityClass(task.priority)}`}>
                      {(task.priority || 'normal').toUpperCase()}
                    </span>
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <span className={`px-3 py-1 text-xs rounded-full ${statusClass(task.status)}`}>
                      {(task.status || 'not_started').replace('_', ' ').toUpperCase()}
                    </span>
                  </ErrorBoundary>
                  <ErrorBoundary>
                    {task.business_id && (
                      <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                        {getBusinessName(task.business_id)}
                      </span>
                    )}
                  </ErrorBoundary>
                  <ErrorBoundary>
                    {safeArray(task.tags).map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800">
                        {tag}
                      </span>
                    ))}
                  </ErrorBoundary>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(task)}
                  className="text-blue-600 hover:text-blue-900"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(task.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Completion Bar */}
            <ErrorBoundary>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">Completion</span>
                <span className="text-sm font-semibold text-gray-900">{safePct(task.completion_percentage)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all"
                  style={{ width: `${safePct(task.completion_percentage)}%` }}
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={completionDrafts[task.id] ?? safePct(task.completion_percentage)}
                  onChange={(e) => setDraftCompletion(task.id, e.target.value)}
                  className="w-48"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={completionDrafts[task.id] ?? safePct(task.completion_percentage)}
                  onChange={(e) => setDraftCompletion(task.id, e.target.value)}
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                />
                <button
                  onClick={() => saveCompletion(task.id)}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
            </ErrorBoundary>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {task.due_date && (
                <div>
                  <span className="text-gray-600">Due Date:</span>
                  <span className={`ml-2 font-medium ${isOverdue(task) ? 'text-red-600' : 'text-gray-900'}`}>
                    {safeFormatDate(task.due_date)}
                  </span>
                </div>
              )}
              {task.assigned_to && (
                <div>
                  <span className="text-gray-600">Assigned To:</span>
                  <span className="ml-2 font-medium text-gray-900">{task.assigned_to}</span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Created:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {safeFormatDate(task.created_at)}
                </span>
              </div>
              {task.completed_date && (
                <div>
                  <span className="text-gray-600">Completed:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {safeFormatDate(task.completed_date)}
                  </span>
                </div>
              )}
            </div>

            {/* Checklist */}
            <ErrorBoundary>
              {safeArray(task.checklist).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Checklist:</h4>
                  <ul className="space-y-1">
                    {safeArray(task.checklist).map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircleIcon className={`h-4 w-4 mr-2 ${item?.completed ? 'text-green-500' : 'text-gray-300'}`} />
                        <span className={item?.completed ? 'line-through text-gray-500' : 'text-gray-700'}>
                          {item?.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </ErrorBoundary>
          </div>
        ))}
        {tasks.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            No tasks found. Click "New Task" to create one.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingTask ? 'Edit Task' : 'New Task'}
            </h2>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="mb-4 text-sm text-blue-600 hover:underline"
            >
              {showHelp ? 'Hide help' : 'Need help?'}
            </button>
            {showHelp && (
              <div className="mb-4 p-3 rounded bg-gray-50 border border-gray-200 text-sm text-gray-700">
                <p className="mb-1">- Title is required.</p>
                <p className="mb-1">- Completion must be between 0 and 100.</p>
                <p className="mb-1">- Checklist must be valid JSON. Example:</p>
                <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">[{`{"text":"Item 1","completed":false}`}]</pre>
              </div>
            )}
            {formError && (
              <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 flex items-start justify-between" role="alert">
                <span>{formError}</span>
                <button
                  type="button"
                  disabled={reporting}
                  onClick={async () => {
                    try {
                      setReporting(true);
                      await api.post('/support', {
                        area: 'tasks',
                        message: formError,
                        url: window.location.href,
                        path: window.location.pathname,
                        payload: { formData, editing: !!editingTask }
                      });
                      alert('Thanks! Your report was sent.');
                    } catch (e) {
                      alert('Could not send report.');
                    } finally {
                      setReporting(false);
                    }
                  }}
                  className="ml-3 text-sm text-blue-700 hover:underline disabled:text-blue-300"
                >Report issue</button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.task_name}
                  onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {formErrors.task_name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.task_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority *</label>
                  <select
                    required
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  {formErrors.priority && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.priority}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {formErrors.status && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.status}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Completion % (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.completion_percentage}
                    onChange={(e) => setFormData({ ...formData, completion_percentage: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {formErrors.completion_percentage && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.completion_percentage}</p>
                )}
              </div>
            </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business</label>
                  <select
                    value={formData.business_id}
                    onChange={(e) => setFormData({ ...formData, business_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">None</option>
                    {businesses.map(business => (
                      <option key={business.id} value={business.id}>{business.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                  <input
                    type="text"
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., urgent, finance, tax"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Checklist
                </label>
                <div className="space-y-2">
                  {checklistItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={item.completed || false}
                        onChange={(e) => updateChecklistItem(index, 'completed', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={item.text || ''}
                        onChange={(e) => updateChecklistItem(index, 'text', e.target.value)}
                        placeholder="Checklist item"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(index)}
                        className="text-red-600 hover:text-red-800 px-2"
                        aria-label="Remove item"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Checklist Item
                  </button>
                </div>
                {formErrors.checklist && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.checklist}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Active</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={(Object.keys(formErrors).length > 0) || saving}
                  className="px-4 py-2 bg-blue-600 disabled:bg-blue-300 text-white rounded-lg hover:bg-blue-700"
                >
                  {saving ? "Saving..." : (editingTask ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

